import { DEFAULT_LOCALE, type SupportedLocale, getPreferredLocale, isSupportedLocale } from "./locales"

const RUNTIME_ONLY_PATHS = new Set(["/pricing/", "/login/", "/register/", "/using-dsg/"])

export const isRuntimeOnlyPath = (pathname: string | null | undefined) =>
  RUNTIME_ONLY_PATHS.has(stripLocalePrefix(pathname))

export const normalizePathname = (pathname: string | null | undefined): string => {
  if (!pathname || pathname === "") return "/"

  let normalized = pathname.trim()
  if (!normalized.startsWith("/")) normalized = `/${normalized}`
  normalized = normalized.replace(/\/{2,}/g, "/")

  if (normalized !== "/" && !normalized.endsWith("/")) normalized = `${normalized}/`
  return normalized
}

export const detectLocaleFromPathname = (pathname: string | null | undefined): SupportedLocale => {
  const normalized = normalizePathname(pathname)
  const [, maybeLocale] = normalized.split("/")
  return isSupportedLocale(maybeLocale) ? maybeLocale : DEFAULT_LOCALE
}

export const stripLocalePrefix = (pathname: string | null | undefined): string => {
  const normalized = normalizePathname(pathname)
  const segments = normalized.split("/").filter(Boolean)

  if (segments.length === 0) return "/"
  if (isSupportedLocale(segments[0])) {
    const remaining = segments.slice(1)
    return remaining.length === 0 ? "/" : `/${remaining.join("/")}/`
  }

  return normalized
}

const localizeBlogPath = (pathname: string, locale: SupportedLocale) => {
  const segments = pathname.split("/").filter(Boolean)

  if (segments[0] !== "blog") return null

  if (segments.length === 1) {
    return locale === DEFAULT_LOCALE ? "/blog/" : `/blog/${locale}/`
  }

  const slugSegments = isSupportedLocale(segments[1]) ? segments.slice(2) : segments.slice(1)
  const slugPath = slugSegments.join("/")

  if (!slugPath) {
    return locale === DEFAULT_LOCALE ? "/blog/" : `/blog/${locale}/`
  }

  return locale === DEFAULT_LOCALE ? `/blog/${slugPath}/` : `/blog/${locale}/${slugPath}/`
}

export const localizeStaticPath = (pathname: string | null | undefined, locale: SupportedLocale): string => {
  const basePath = stripLocalePrefix(pathname)
  const blogPath = localizeBlogPath(basePath, locale)

  if (blogPath) return blogPath
  if (basePath === "/") return locale === DEFAULT_LOCALE ? "/" : `/${locale}/`

  return locale === DEFAULT_LOCALE ? basePath : `/${locale}${basePath}`
}

export const localizeRuntimePath = (
  pathname: string | null | undefined,
  locale: SupportedLocale,
  search: string | null | undefined
): string => {
  const normalized = normalizePathname(pathname)
  const params = new URLSearchParams(search ?? "")

  if (locale === DEFAULT_LOCALE) {
    params.delete("lang")
  } else {
    params.set("lang", locale)
  }

  const query = params.toString()
  return query ? `${normalized}?${query}` : normalized
}

export const localizePath = (
  pathname: string | null | undefined,
  locale: SupportedLocale,
  search: string | null | undefined = ""
): string => {
  const basePath = stripLocalePrefix(pathname)

  if (isRuntimeOnlyPath(basePath)) {
    return localizeRuntimePath(basePath, locale, search)
  }

  return localizeStaticPath(basePath, locale)
}

export const getLocalizedLink = (href: string, locale: SupportedLocale): string => {
  if (href.startsWith("#")) {
    const homePath = locale === DEFAULT_LOCALE ? "/" : `/${locale}/`
    return `${homePath}${href}`
  }

  const [pathnamePart, hashPart] = href.split("#")
  const [pathname, search = ""] = pathnamePart.split("?")
  const target = localizePath(pathname, locale, search ? `?${search}` : "")

  return hashPart ? `${target}#${hashPart}` : target
}

export const getLocaleFromSearch = (search: string | null | undefined): SupportedLocale | null => {
  const params = new URLSearchParams(search ?? "")
  const lang = params.get("lang")
  return isSupportedLocale(lang) ? lang : null
}

export const resolveInitialLocale = (
  pathname: string | null | undefined,
  search: string | null | undefined,
  fallback?: string | null
): SupportedLocale => {
  const queryLocale = getLocaleFromSearch(search)
  if (queryLocale) return queryLocale

  const routeLocale = detectLocaleFromPathname(pathname)
  if (routeLocale !== DEFAULT_LOCALE) return routeLocale

  return getPreferredLocale(fallback)
}
